import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { type Track, useAdminCreateTrack, useAdminUpdateTrack, useAdminUsers } from "@/lib/api";

const NO_OWNER = "__none__";
const NO_OUTCOME = "__none__";
const NOT_SET = "__unset__";

const STAGE_OPTIONS = [
  { value: "not_started", label: "Not started" },
  { value: "discover", label: "Discover" },
  { value: "prioritize", label: "Prioritize" },
  { value: "prototype", label: "Prototype" },
  { value: "board_prep", label: "Board Prep" },
  { value: "paused", label: "Paused" },
  { value: "blocked", label: "Blocked" },
];

const PROTOTYPE_RAG_OPTIONS = [
  { value: "green", label: "Green" },
  { value: "amber", label: "Amber" },
  { value: "red", label: "Red" },
];

const VALUE_CONFIDENCE_OPTIONS = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "tbc", label: "TBC" },
];

const BOARD_READINESS_OPTIONS = [
  { value: "not_started", label: "Not started" },
  { value: "early", label: "Early" },
  { value: "in_progress", label: "In progress" },
  { value: "ready", label: "Ready" },
  { value: "at_risk", label: "At risk" },
  { value: "blocked", label: "Blocked" },
];

const BOARD_OUTCOME_OPTIONS = [
  { value: "on_track", label: "On track" },
  { value: "monitor", label: "Monitor" },
  { value: "at_risk", label: "At risk" },
  { value: "blocked", label: "Blocked" },
];

interface Props {
  track: Track | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Parse a numeric input string into a number | null (empty → null). */
function toNumberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function TrackFormDialog({ track, open, onOpenChange }: Props) {
  const usersQuery = useAdminUsers();
  const createMutation = useAdminCreateTrack();
  const updateMutation = useAdminUpdateTrack();

  const isEdit = !!track;

  const [name, setName] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState(NO_OWNER);
  const [targetDate, setTargetDate] = useState("");
  const [isActive, setIsActive] = useState("true");

  // Pipeline fields
  const [stage, setStage] = useState("not_started");
  const [selectedUseCase, setSelectedUseCase] = useState("");
  const [championNote, setChampionNote] = useState("");
  const [useCasesIdentified, setUseCasesIdentified] = useState("");
  const [useCasesPrioritized, setUseCasesPrioritized] = useState("");
  const [prototypeNote, setPrototypeNote] = useState("");
  const [prototypeRag, setPrototypeRag] = useState(NOT_SET);
  const [valueConfidence, setValueConfidence] = useState(NOT_SET);
  const [boardReadiness, setBoardReadiness] = useState(NOT_SET);
  const [boardOutcome, setBoardOutcome] = useState(NO_OUTCOME);

  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setName(track?.name ?? "");
      setBusinessUnit(track?.business_unit ?? "");
      setDescription(track?.description ?? "");
      setOwnerId(track?.owner_id ?? NO_OWNER);
      setTargetDate(track?.target_date ?? "");
      setIsActive(track?.is_active === false ? "false" : "true");
      setStage(track?.stage ?? "not_started");
      setSelectedUseCase(track?.selected_use_case ?? "");
      setChampionNote(track?.champion_note ?? "");
      setUseCasesIdentified(
        track?.use_cases_identified != null ? String(track.use_cases_identified) : "",
      );
      setUseCasesPrioritized(
        track?.use_cases_prioritized != null ? String(track.use_cases_prioritized) : "",
      );
      setPrototypeNote(track?.prototype_note ?? "");
      setPrototypeRag(track?.prototype_rag ?? NOT_SET);
      setValueConfidence(track?.value_confidence ?? NOT_SET);
      setBoardReadiness(track?.board_readiness ?? NOT_SET);
      setBoardOutcome(track?.board_outcome ?? NO_OUTCOME);
      setError("");
    }
  }, [open, track]);

  const users = usersQuery.data ?? [];

  const handleSave = async () => {
    setError("");
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const owner = ownerId === NO_OWNER ? undefined : ownerId;
    const outcome = boardOutcome === NO_OUTCOME ? null : boardOutcome;
    const pipeline = {
      stage,
      selectedUseCase: selectedUseCase.trim() || null,
      championNote: championNote.trim() || null,
      useCasesIdentified: toNumberOrNull(useCasesIdentified),
      useCasesPrioritized: toNumberOrNull(useCasesPrioritized),
      prototypeNote: prototypeNote.trim() || null,
      prototypeRag: prototypeRag === NOT_SET ? null : prototypeRag,
      valueConfidence: valueConfidence === NOT_SET ? null : valueConfidence,
      boardReadiness: boardReadiness === NOT_SET ? null : boardReadiness,
      boardOutcome: outcome,
    };
    try {
      if (isEdit && track) {
        await updateMutation.mutateAsync({
          trackId: track.id,
          name,
          businessUnit: businessUnit || null,
          description: description || null,
          ownerId: owner ?? null,
          targetDate: targetDate || undefined,
          isActive: isActive === "true",
          ...pipeline,
        });
      } else {
        await createMutation.mutateAsync({
          name,
          businessUnit: businessUnit || undefined,
          description: description || undefined,
          ownerId: owner,
          targetDate: targetDate || undefined,
          ...pipeline,
        });
      }
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save track.");
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit track" : "New track"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="trackName">Name</Label>
            <Input id="trackName" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="trackBu">Business unit</Label>
            <Input
              id="trackBu"
              value={businessUnit}
              onChange={(e) => setBusinessUnit(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="trackDesc">Description</Label>
            <Textarea
              id="trackDesc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="trackOwner">Owner</Label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger id="trackOwner">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_OWNER}>Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name || u.last_name
                        ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()
                        : u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="trackTarget">Target date</Label>
              <Input
                id="trackTarget"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            </div>
          </div>
          {isEdit && (
            <div className="space-y-2">
              <Label htmlFor="trackActive">State</Label>
              <Select value={isActive} onValueChange={setIsActive}>
                <SelectTrigger id="trackActive">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ── Use-case pipeline ── */}
          <div className="border-border space-y-4 border-t pt-4">
            <p className="text-muted-foreground text-xs font-medium uppercase">Use-case pipeline</p>
            <div className="space-y-2">
              <Label htmlFor="trackSelectedUseCase">Selected use case</Label>
              <Input
                id="trackSelectedUseCase"
                value={selectedUseCase}
                onChange={(e) => setSelectedUseCase(e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="trackStage">Stage</Label>
                <Select value={stage} onValueChange={setStage}>
                  <SelectTrigger id="trackStage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="trackProtoRag">Prototype status</Label>
                <Select value={prototypeRag} onValueChange={setPrototypeRag}>
                  <SelectTrigger id="trackProtoRag">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NOT_SET}>— (not set)</SelectItem>
                    {PROTOTYPE_RAG_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="trackChampNote">Champion note</Label>
              <Input
                id="trackChampNote"
                value={championNote}
                onChange={(e) => setChampionNote(e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="trackUcId">Use cases identified</Label>
                <Input
                  id="trackUcId"
                  type="number"
                  min={0}
                  value={useCasesIdentified}
                  onChange={(e) => setUseCasesIdentified(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="trackUcPrio">Use cases prioritized</Label>
                <Input
                  id="trackUcPrio"
                  type="number"
                  min={0}
                  value={useCasesPrioritized}
                  onChange={(e) => setUseCasesPrioritized(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="trackProtoNote">Prototype note</Label>
              <Input
                id="trackProtoNote"
                value={prototypeNote}
                onChange={(e) => setPrototypeNote(e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="trackValueConf">Value confidence</Label>
                <Select value={valueConfidence} onValueChange={setValueConfidence}>
                  <SelectTrigger id="trackValueConf">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NOT_SET}>— (not set)</SelectItem>
                    {VALUE_CONFIDENCE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="trackBoardReady">Board readiness</Label>
                <Select value={boardReadiness} onValueChange={setBoardReadiness}>
                  <SelectTrigger id="trackBoardReady">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NOT_SET}>— (not set)</SelectItem>
                    {BOARD_READINESS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="trackBoardOutcome">Board outcome</Label>
              <Select value={boardOutcome} onValueChange={setBoardOutcome}>
                <SelectTrigger id="trackBoardOutcome">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_OUTCOME}>None</SelectItem>
                  {BOARD_OUTCOME_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
